import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import analytics from '../../utils/analytics';
import { IconPlay, IconArrowRight } from '../ui/icons';

/**
 * 🎬 VIDÉO DE PRÉSENTATION (page d'accueil, juste sous le carrousel)
 * Visite commentée de la plateforme (2 min 26 s, voix off et sous-titres en français).
 * preload="none" : rien n'est téléchargé tant que le visiteur ne lance pas la vidéo,
 * pour ménager les forfaits mobiles. Les chapitres placent la lecture au bon moment.
 */

const BASE = `${process.env.PUBLIC_URL || ''}/assets/media`;

const CHAPTERS = [
  { time: 5.5, label: 'Accueil et projets' },
  { time: 24.5, label: 'La fiche d’un projet' },
  { time: 34.5, label: 'Espace diaspora' },
  { time: 44, label: 'Financer mon projet' },
  { time: 63, label: 'Incubateur du Centre Hub' },
  { time: 72.5, label: 'L’équipe et le contact' },
  { time: 91.5, label: 'Nos projets' },
];

const formatTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const PresentationVideo = () => {
  const videoRef = useRef(null);

  const playFrom = (time) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = time;
    video.play().catch(() => {});
    video.scrollIntoView({ behavior: 'smooth', block: 'center' });
    analytics.trackClick('home_presentation_chapter', { time });
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-900 to-blue-900 text-white" aria-labelledby="presentation-video-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 lg:gap-x-10 lg:grid-cols-5 lg:items-center">
        <div className="lg:col-span-2 lg:self-end">
          <p className="inline-flex items-center gap-2 rounded-full bg-orange-500/20 px-3 py-1 text-sm font-semibold text-orange-300">
            <IconPlay aria-hidden="true" /> Vidéo · 2 min 26
          </p>
          <h2 id="presentation-video-title" className="mt-4 text-3xl md:text-4xl font-bold">
            Découvrez ImpactInvest en 2 minutes
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Une visite guidée de la plateforme : comment choisir un projet, investir depuis le Niger ou l’étranger,
            et faire financer votre propre entreprise.
          </p>
        </div>

        <div className="lg:col-span-3 lg:row-span-2">
          <div className="relative">
            <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-orange-500 to-green-500 opacity-40 blur-lg" aria-hidden="true" />
            <video
              ref={videoRef}
              className="relative w-full aspect-video rounded-xl bg-black shadow-2xl"
              poster={`${BASE}/presentation-plateforme-poster.jpg`}
              controls
              playsInline
              preload="none"
              onPlay={() => analytics.trackClick('home_presentation_play', {})}
            >
              <source src={`${BASE}/presentation-plateforme.mp4`} type="video/mp4" />
              <track kind="captions" src={`${BASE}/presentation-plateforme.fr.vtt`} srcLang="fr" label="Français" />
              Votre navigateur ne peut pas lire cette vidéo.
            </video>
          </div>
          <p className="mt-3 text-xs text-blue-200">
            Voix off générée par intelligence artificielle. Tout investissement comporte un risque.
          </p>
        </div>

        <div className="lg:col-span-2 lg:self-start">
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CHAPTERS.map((c) => (
              <li key={c.time}>
                <button
                  type="button"
                  onClick={() => playFrom(c.time)}
                  className="flex w-full items-center gap-3 rounded-lg bg-white/5 px-3 py-2 text-left text-sm hover:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                >
                  <span className="font-mono text-orange-300">{formatTime(c.time)}</span>
                  <span>{c.label}</span>
                </button>
              </li>
            ))}
          </ol>

          <Link
            to="/projects"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl shadow-lg transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
          >
            Voir les projets <IconArrowRight aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PresentationVideo;
